import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import PlotTrackAddRow from './PlotTrackAddRow';
import PlotTrackAddScene from './PlotTrackAddScene';
import PlotTrackChapterSeed from './PlotTrackChapterSeed';
import PlotTrackGenerate from './PlotTrackGenerate';
import OutlineCardRows from './OutlineCardRows';
import PlotTrackGrid from './PlotTrackGrid';
import PlotTrackWhenLabels from './PlotTrackWhenLabels';
import PlotTrackInspector from './PlotTrackInspector';
import PlotTrackTray from './PlotTrackTray';
import PlotTrackImportMap from './PlotTrackImportMap';
import UploadSettle from '../UploadGaps/UploadSettle';
import NarrativePovStrip from '../NarrativePov/NarrativePovSettle';
import BookStructureDoor from '../Frameworks/BookStructureDoor';
import BookToolsGroup from '../BookTools/BookToolsGroup';
import { publishChatFocus, publishChatSceneRemoved } from '../Chat/ChatPaneFrame';
import JourneyNextTip from '../Common/JourneyNextTip';
import { BottomSheet } from '../Common/BottomSheet';
import PersonControl from '../Common/PersonControl';
import WhoseHeadControl from '../Common/WhoseHeadControl';
import { peopleForPov, styleCardsApi } from '../../api/styleCards';
import { sectionPersonBody } from '../../utils/personMode';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import {
  cellAt,
  cellsForColumn,
  columnDisplayName,
  columnHandoff,
  columnIsSequence,
  columnNeedsDraftGate,
  filterColumnsByWhoseHead,
  whoseHeadOptions,
  whoseHeadValue,
} from '../../utils/plottrackGrid';
import { journeyNextTip } from '../../utils/journeyNextTip';
import { readPendingOffer, readPendingOffers } from '../../utils/plottrackOfferPersist';
import { draftThisScenePath } from '../../utils/sceneWritingPath';
import { uploadPathApi } from '../../api/uploadPath';
import { importedSceneFor, presentGaps, scenePurposeBlocksDraft } from '../../utils/uploadGaps';
import { castIdsFromBookPov, povNeedsSettle } from '../../utils/narrativePov';
import {
  DRAFT_PACK_ANYWAY,
  DRAFT_PACK_TITLE,
  DRAFT_PACK_WARN,
  OUTLINE_ROOM_TITLE,
  EMPTY_GRID_LEAD,
  OUTLINE_EMPTY_FRAMEWORK_BODY,
  OUTLINE_EMPTY_FRAMEWORK_TITLE,
  OUTLINE_EMPTY_WRITE_BODY,
  OUTLINE_EMPTY_WRITE_TITLE,
  OUTLINE_MORE,
  OUTLINE_MORE_CHECK,
  OUTLINE_MORE_GAPS,
  OUTLINE_MORE_GAPS_FAILED,
  OUTLINE_MORE_GAPS_ON,
  OUTLINE_MORE_POV,
  OUTLINE_MORE_RULES,
  OUTLINE_MORE_VIEW,
  OUTLINE_VIEW_AS,
  OUTLINE_VIEW_CARDS,
  OUTLINE_VIEW_GRID,
  OUTLINE_GRID_SIZE,
  OUTLINE_GRID_SIZE_COMFORTABLE,
  OUTLINE_GRID_SIZE_COMPACT,
  OUTLINE_MORE_VISIBLE,
  CHAPTER_BREAK_REMOVED,
  SCENE_DELETED,
  SCENE_MOVED,
  RECENTLY_DELETED,
  PT_PANEL_CLOSE,
  PT_TOOL_ARRANGE,
  PT_TOOL_BUILD,
  PT_POV_FILTER_ALL,
  PT_POV_FILTER_HINT,
  PT_POV_FILTER_LABEL,
  PT_POV_FILTER_GROUP,
  PT_POV_FILTER_PERSON,
  PT_NAMING_FILTER_PERSON,
  PT_PEOPLE_FILTER_GROUP,
  PT_WIDTH_LABEL,
  PT_WIDTH_OPTION,
  PT_WIDTH_NARROW,
  PT_WIDTH_RESET,
  PT_WIDTH_STANDARD,
  PT_WIDTH_WIDE,
  PT_WHEN_FILTER_GROUP,
  PT_WHEN_HIDE,
  PT_WHEN_SHOW,
  PT_TIMELINE_FAILED,
  PT_TIMELINE_HINT,
  PT_TIMELINE_OFFER,
  PT_TIMELINE_OFFER_PART,
  PT_TIMELINE_USE,
  PT_TIMELINE_USING,
  SPAN_CONTINUITY_LEDE,
  WHOSE_HEAD,
  WHOSE_HEAD_GO_BACK,
  spanContinuityDetails,
} from '../../utils/writerCopy';
import useBookPath, { noteBookPathChanged, OUTLINE_CHANGED } from '../../hooks/useBookPath';
import { allScenesAccepted, bookPathProgress, bookPathTip, outlineNextWrite } from '../../utils/bookPathTip';
import { plotDossiersApi } from '../../api/plotDossiers';
import { resolveStoryFrameworkMeta } from '../../api/frameworks';
import UndoMessage from './UndoMessage';
import RecentlyDeleted from './RecentlyDeleted';
import { restoreRecentlyDeleted } from '../../api/plottrack';
import { scenePlace, undoMove } from './sceneUndo';
import { CHAT_TEXT_SIZE, readChatZoom } from '../../utils/textSizeRange';
import './PlotTrackLayout.css';

const WHEN_FILTER_PREFIX = 'when:';
// Same pattern as the relationship graph view (brainstormy.jetage.graphView):
// one choice for this writer, remembered in the browser. Cards when unset.
const OUTLINE_VIEW_KEY = 'brainstormy.jetage.outlineView';
// One choice for this writer, same pattern as outlineView. Not per book.
const OUTLINE_GRID_SIZE_KEY = 'brainstormy.jetage.outlineGridSize';

function readOutlineView() {
  try {
    return localStorage.getItem(OUTLINE_VIEW_KEY) === 'grid' ? 'grid' : 'cards';
  } catch {
    return 'cards';
  }
}

function readOutlineGridSize() {
  try {
    return localStorage.getItem(OUTLINE_GRID_SIZE_KEY) === 'compact' ? 'compact' : 'comfortable';
  } catch {
    return 'comfortable';
  }
}

/** One notice at a time. The first active key wins; the rest wait. */
export function pickOutlineNotice(active) {
  const order = ['outcome', 'upload', 'continuity', 'when', 'gap', 'journey'];
  return order.find((key) => active && active[key]) || null;
}

/** Run 53: the timelines Generate named for scenes with no When label, in story order. */
export function timelineOffers(columns) {
  const counts = new Map();
  (columns || []).forEach((column) => {
    const offer = column?.when_offer;
    if (column?.when || !offer || offer.from !== 'timeline' || !offer.label) return;
    counts.set(offer.label, (counts.get(offer.label) || 0) + 1);
  });
  return Array.from(counts.entries()).map(([label, count]) => ({ label, count }));
}

function TimelineOffer({ offers, onUse, onDone }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (!offers.length || !onUse) return null;
  const parts = offers.map((offer) => PT_TIMELINE_OFFER_PART(offer.label, offer.count)).join(', ');
  const use = async () => {
    setBusy(true);
    setError(null);
    try {
      await onUse();
      onDone?.();
    } catch {
      setError(PT_TIMELINE_FAILED);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="pt-timeline-offer" role="group" aria-label={PT_TIMELINE_USE} data-testid="pt-timeline-offer">
      <p className="pt-timeline-offer__text">{PT_TIMELINE_OFFER(parts)}</p>
      <button type="button" className="pt-toolbar__btn" onClick={use} disabled={busy}>
        {busy ? PT_TIMELINE_USING : PT_TIMELINE_USE}
      </button>
      <span className="pt-timeline-offer__hint">{PT_TIMELINE_HINT}</span>
      {error && <p className="pt-timeline-offer__error" role="alert">{error}</p>}
    </div>
  );
}
const WITH_FILTER_PREFIX = 'with:';
const PLACEHOLDER_SEQUENCE = /^(sequence|act|chapter)\s*\d+$/i;

function sequenceTitle(value) {
  const name = String(value || '').trim();
  if (!name || PLACEHOLDER_SEQUENCE.test(name)) return '';
  return name;
}

function frameworkRefIsSet(ref) {
  if (ref == null) return false;
  const text = String(ref).trim().toLowerCase();
  return text !== '' && text !== 'none';
}

function beatNames(dto) {
  return (dto?.beats || [])
    .map((beat) => sequenceTitle(beat?.name))
    .filter(Boolean);
}

// A ready-made Framework already names its sequences. Those names are the
// rows to show before any scene exists.
function namedSequenceTitles(headers, frameworkNames) {
  const fromFramework = (frameworkNames || []).map(sequenceTitle).filter(Boolean);
  if (fromFramework.length) return fromFramework;
  return (headers?.sequences || [])
    .map((sequence) => sequenceTitle(sequence?.display_name))
    .filter(Boolean);
}

export default function PlotTrackLayout({
  storyName,
  board,
  currentStoryId,
  projectId = null,
  startGenerate = false,
  handoffId = '',
  isSeries,
}) {
  const navigate = useNavigate();
  const breakpoint = useBreakpoint();
  const narrow = breakpoint === 'mobile';
  const [selected, setSelected] = useState(null);
  const [whoseHead, setWhoseHead] = useState('');
  const [whenToggle, setWhenToggle] = useState(null);
  const [spanFlags, setSpanFlags] = useState([]);
  const [outline, setOutline] = useState(null);
  const [packWarn, setPackWarn] = useState(false);
  const [pathView, setPathView] = useState(null);
  const [bookPov, setBookPov] = useState(null);
  const [outlineNonce, setOutlineNonce] = useState(0);
  const [frameworkNames, setFrameworkNames] = useState([]);
  const [frameworkHeld, setFrameworkHeld] = useState(false);
  const frameworkNamesStoryRef = useRef(null);
  // Set when this visit hangs a Framework, before the board reload lands.
  // A stale status read must not put the start screen back.
  const frameworkHungStoryRef = useRef(null);
  // Set when this visit removes a Framework, so a stale board reference
  // does not paint the sequences again before reload.
  const frameworkClearedStoryRef = useRef(null);
  // Build outline was open before any scene. Close it once scenes exist.
  const openedBuildEmptyRef = useRef(false);
  // Grid first: 'build' (Generate) or 'arrange' (add, chapters, unplaced), or closed.
  // An unresolved Generate draft opens Build again on the way back to this book.
  const [toolPanel, setToolPanel] = useState(() => (
    startGenerate || readPendingOffer(currentStoryId) ? 'build' : null
  ));
  // Empty grids mount Generate only while this is on. A waiting outline
  // has to turn it on even when the writer did not arrive from Idea.
  const [showSuggest, setShowSuggest] = useState(() => (
    Boolean(startGenerate) || Boolean(readPendingOffer(currentStoryId))
  ));
  const [generateOutcome, setGenerateOutcome] = useState(null);
  const handoffOpened = useRef(false);
  const storyForHandoff = useRef(currentStoryId);
  const rememberOutcome = useCallback((next) => {
    setGenerateOutcome(next && next.text ? next : null);
  }, []);
  const keepGenerateOpen = useCallback(() => {
    handoffOpened.current = true;
    setToolPanel('build');
    setShowSuggest(true);
  }, []);
  const [planPart, setPlanPart] = useState(null);
  const [planPinned, setPlanPinned] = useState(false);
  const [undo, setUndo] = useState(null);
  const [chapterError, setChapterError] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [gapNote, setGapNote] = useState('');
  const [showPov, setShowPov] = useState(false);
  const [viewMenuOpen, setViewMenuOpen] = useState(false);
  const [outlineView, setOutlineView] = useState(readOutlineView);
  const [outlineGridSize, setOutlineGridSize] = useState(readOutlineGridSize);
  const chooseOutlineView = (next) => {
    const view = next === 'grid' ? 'grid' : 'cards';
    setOutlineView(view);
    try {
      localStorage.setItem(OUTLINE_VIEW_KEY, view);
    } catch {
      // The choice still holds for this visit when storage is blocked.
    }
  };
  const chooseOutlineGridSize = (next) => {
    const size = next === 'compact' ? 'compact' : 'comfortable';
    setOutlineGridSize(size);
    try {
      localStorage.setItem(OUTLINE_GRID_SIZE_KEY, size);
    } catch {
      // The choice still holds for this visit when storage is blocked.
    }
  };
  const sequences = board.headers?.sequences || [];
  const filterPeople = useMemo(() => whoseHeadOptions(board.columns), [board.columns]);
  const povPeople = useMemo(() => peopleForPov(outline), [outline]);
  const people = useMemo(
    () => povPeople.map((person) => ({
      id: person.pickId || person.character_entity_id || person.bible_entry_id,
      name: person.name,
    })).filter((person) => person.id && person.name),
    [povPeople],
  );
  const whenLabels = board.whenLabels || [];
  // The When row shows once the book has a label, or when the writer turns it on (spec §1).
  const showWhen = whenToggle === null ? whenLabels.length > 0 : whenToggle;
  // People named in at least one scene, for the "Scenes naming" filter (spec §2).
  const namedPeople = useMemo(() => {
    const ids = new Set();
    (board.columns || []).forEach((column) => (column.people || []).forEach((p) => ids.add(String(p.id))));
    return (board.peoplePool || []).filter((person) => ids.has(String(person.id)));
  }, [board.columns, board.peoplePool]);
  const visibleColumns = useMemo(
    () => (String(whoseHead).startsWith(WITH_FILTER_PREFIX)
      ? (board.columns || []).filter((column) => (column.people || []).some(
        (p) => String(p.id) === String(whoseHead).slice(WITH_FILTER_PREFIX.length),
      ))
      : String(whoseHead).startsWith(WHEN_FILTER_PREFIX)
      ? (board.columns || []).filter(
        (column) => String(column.when?.id || '') === String(whoseHead).slice(WHEN_FILTER_PREFIX.length),
      )
      : filterColumnsByWhoseHead(board.columns, whoseHead)),
    [board.columns, whoseHead],
  );

  useEffect(() => {
    const applyTextSize = () => {
      let raw = null;
      try {
        raw = localStorage.getItem(CHAT_TEXT_SIZE.storageKey);
      } catch {
        raw = null;
      }
      const percent = readChatZoom(raw);
      document.documentElement.style.setProperty('--zoom-level', String(percent / 100));
    };
    applyTextSize();
    window.addEventListener('storage', applyTextSize);
    return () => window.removeEventListener('storage', applyTextSize);
  }, []);

  useEffect(() => {
    if (!currentStoryId) { setOutline(null); return undefined; }
    let cancelled = false;
    styleCardsApi.outline(currentStoryId)
      .then((next) => { if (!cancelled) setOutline(next); })
      .catch(() => { if (!cancelled) setOutline({}); });
    return () => { cancelled = true; };
  }, [currentStoryId, outlineNonce]);

  useEffect(() => {
    if (!currentStoryId || typeof window === 'undefined') return undefined;
    const onOutline = () => {
      setOutlineNonce((nonce) => nonce + 1);
    };
    window.addEventListener(OUTLINE_CHANGED, onOutline);
    return () => window.removeEventListener(OUTLINE_CHANGED, onOutline);
  }, [currentStoryId]);

  useEffect(() => {
    if (!currentStoryId) {
      frameworkNamesStoryRef.current = null;
      frameworkHungStoryRef.current = null;
      frameworkClearedStoryRef.current = null;
      setFrameworkNames([]);
      setFrameworkHeld(false);
      return undefined;
    }
    // Another book's names must not stay on screen. A failed read on this
    // book keeps the names already loaded for it, so the cards do not
    // renumber every outline sequence.
    if (frameworkNamesStoryRef.current !== currentStoryId) {
      frameworkHungStoryRef.current = null;
      frameworkClearedStoryRef.current = null;
      setFrameworkNames([]);
      setFrameworkHeld(frameworkRefIsSet(board.framework_ref));
    }
    let cancelled = false;
    const boardRef = board.framework_ref;
    (async () => {
      try {
        const status = await plotDossiersApi.status(currentStoryId);
        const statusRef = status?.framework_ref;
        let ref = null;
        if (frameworkRefIsSet(statusRef)) {
          ref = statusRef;
          frameworkClearedStoryRef.current = null;
        } else if (
          frameworkRefIsSet(boardRef)
          && frameworkClearedStoryRef.current !== currentStoryId
        ) {
          ref = boardRef;
        }
        if (!frameworkRefIsSet(ref)) {
          if (cancelled) return;
          // Use this Framework just stored the reference. Status can still
          // say none for a moment. Keep the grid up.
          if (frameworkHungStoryRef.current === currentStoryId) {
            setFrameworkHeld(true);
            return;
          }
          frameworkNamesStoryRef.current = currentStoryId;
          setFrameworkNames([]);
          setFrameworkHeld(false);
          return;
        }
        if (!cancelled) setFrameworkHeld(true);
        const meta = await resolveStoryFrameworkMeta(ref);
        const names = beatNames(meta?.dto);
        if (!cancelled) {
          frameworkNamesStoryRef.current = currentStoryId;
          // An empty beat list must not wipe names already on screen.
          // That empty read used to bring the start screen back.
          if (names.length) setFrameworkNames(names);
        }
      } catch {
        // A failed status read must not wipe the sequence names. The cards
        // would then number every outline sequence, including one the
        // Framework folds onto the first card.
        if (!cancelled && frameworkRefIsSet(boardRef)) setFrameworkHeld(true);
      }
    })();
    return () => { cancelled = true; };
  }, [currentStoryId, outlineNonce, board.framework_ref]);

  useEffect(() => {
    if (!selected?.column) return;
    const selectedId = selected.column.scene_id || selected.column.id;
    const column = (board.columns || []).find((col) => (
      String(col.scene_id) === String(selectedId) || String(col.id) === String(selectedId)
    ));
    if (column && column !== selected.column) {
      setSelected((prev) => (prev ? { ...prev, column } : prev));
    }
  }, [board.columns, selected]);

  const onSelectCell = (row, column, cell) => {
    setSelected({ row, column, cell: cellAt(board.cells, row.id, column.id) });
  };

  const placeScene = async (sceneId, place, origin) => {
    const from = origin || scenePlace(board.headers, sceneId);
    await board.editScene({
      sceneId,
      sequenceId: place.sequenceId,
      afterSceneId: place.afterSceneId,
      atStart: place.atStart,
    });
    const same = from
      && String(from.sequenceId || '') === String(place.sequenceId || '')
      && Boolean(from.atStart) === Boolean(place.atStart)
      && String(from.afterSceneId || '') === String(place.afterSceneId || '');
    if (!from || same) {
      setUndo(null);
      return;
    }
    const restore = undoMove(from, sceneId);
    setUndo({
      message: SCENE_MOVED,
      run: () => board.editScene(restore),
    });
  };

  useEffect(() => {
    const storyChanged = storyForHandoff.current !== currentStoryId;
    storyForHandoff.current = currentStoryId;
    setSpanFlags([]);
    setPackWarn(false);
    setGapNote('');
    if (storyChanged) {
      handoffOpened.current = false;
      setGenerateOutcome(null);
    }
    if (startGenerate) handoffOpened.current = true;
    if (handoffOpened.current) {
      setToolPanel('build');
      setShowSuggest(true);
      return;
    }
    if (readPendingOffer(currentStoryId)) {
      setToolPanel('build');
      setShowSuggest(true);
      return;
    }
    setToolPanel(startGenerate ? 'build' : null);
    setShowSuggest(Boolean(startGenerate));
  }, [currentStoryId, startGenerate]);

  useEffect(() => {
    const scenes = (board.columns || []).length > 0;
    if (!scenes && (toolPanel === 'build' || showSuggest)) {
      openedBuildEmptyRef.current = true;
    }
    if (!scenes || !openedBuildEmptyRef.current) return;
    if (readPendingOffer(currentStoryId)) return;
    openedBuildEmptyRef.current = false;
    setToolPanel((current) => (current === 'build' ? null : current));
    setShowSuggest(false);
    setPlanPinned(false);
    setPlanPart(null);
  }, [board.columns, toolPanel, showSuggest, currentStoryId]);

  useEffect(() => {
    if (!currentStoryId) {
      setPathView(null);
      return undefined;
    }
    let cancelled = false;
    Promise.resolve(uploadPathApi.view(currentStoryId))
      .then((data) => { if (!cancelled) setPathView(data || null); })
      .catch(() => { if (!cancelled) setPathView(null); });
    return () => { cancelled = true; };
  }, [currentStoryId]);

  const continuityLines = useMemo(
    () => spanContinuityDetails(spanFlags),
    [spanFlags],
  );

  const onSave = async (payload) => {
    const saved = await board.saveCell(payload);
    setSelected((prev) => (prev ? { ...prev, cell: saved } : prev));
    return saved;
  };

  const onSaveSpan = async (payload) => {
    const next = await board.setHeaderSpan(payload);
    setSpanFlags(Array.isArray(next?.continuity_flags) ? next.continuity_flags : []);
    return next;
  };

  const resolvedColumn = () => {
    const selectedId = selected?.column?.scene_id || selected?.column?.id;
    return (board.columns || []).find((col) => (
      String(col.scene_id) === String(selectedId) || String(col.id) === String(selectedId)
    )) || selected?.column;
  };

  const goDraftScene = () => {
    const column = resolvedColumn();
    if (!column?.scene_id || !currentStoryId) return;
    if (scenePurposeBlocksDraft(pathView?.gaps, column.scene_id)) return;
    const related = cellsForColumn(column, board.cells);
    setPackWarn(false);
    navigate(draftThisScenePath(currentStoryId, columnHandoff(column, selected?.cell, related)));
  };

  const continueAfterPackWarn = () => {
    setPackWarn(false);
    const column = resolvedColumn();
    if (!column?.scene_id || !currentStoryId) return;
    goDraftScene();
  };

  const gaps = pathView?.gaps || null;

  const onFocusScene = (sceneId) => {
    const column = (board.columns || []).find((col) => String(col.scene_id) === String(sceneId));
    if (!column) return;
    const row = (board.rows || [])[0] || null;
    setSelected({
      row,
      column,
      cell: row ? cellAt(board.cells, row.id, column.id) : null,
    });
  };

  const onSavePurpose = async (sceneId, purpose) => {
    const next = await uploadPathApi.setPurpose(currentStoryId, { sceneId, purpose });
    setPathView(next);
  };

  const onRenameScene = async (sceneId, name) => {
    const next = await uploadPathApi.renameScene(currentStoryId, { sceneId, name });
    setPathView(next);
    if (board.reload) await board.reload({ silent: true });
  };

  // Run 51: titles for the panel's "Goes" choice, as the grid shows them.
  const sceneTitles = useMemo(() => {
    const out = {};
    (board.columns || []).forEach((column) => {
      if (column?.scene_id) {
        out[String(column.scene_id)] = columnDisplayName(column, cellsForColumn(column, board.cells));
      }
    });
    return out;
  }, [board.columns, board.cells]);

  // Run 48: rename or move the selected scene; the reload re-reads its column.
  const onEditScene = (payload) => board.editScene(payload);

  const onOpenNotes = projectId && currentStoryId
    ? () => navigate(`/projects/${projectId}/stories/${currentStoryId}/notes`)
    : undefined;

  const onDraftScene = () => {
    const column = resolvedColumn();
    if (!column?.scene_id || !currentStoryId) return;
    if (scenePurposeBlocksDraft(gaps, column.scene_id)) return;
    if (columnNeedsDraftGate(column, board.cells)) {
      setPackWarn(true);
      return;
    }
    continueAfterPackWarn();
  };

  // #2263: the scene title (and the rest of that header, outside the menu)
  // opens the scene the same way Next scene and Draft this scene do.
  const openSceneColumn = (column) => {
    if (!column?.scene_id || !currentStoryId) return;
    const row = (board.rows || [])[0] || null;
    const related = cellsForColumn(column, board.cells);
    const cell = row ? cellAt(board.cells, row.id, column.id) : null;
    if (row) setSelected({ row, column, cell });
    if (scenePurposeBlocksDraft(gaps, column.scene_id)) return;
    if (columnNeedsDraftGate(column, board.cells)) {
      setPackWarn(true);
      return;
    }
    setPackWarn(false);
    navigate(draftThisScenePath(currentStoryId, columnHandoff(column, cell, related)));
  };

  const onSetWhoseHead = async (sceneId, next) => {
    if (!board.setWhoseHead) return;
    await board.setWhoseHead(sceneId, next);
  };

  const onSetPerson = async (sceneId, mode) => {
    if (!currentStoryId || !sceneId) return;
    await styleCardsApi.setSectionStyle(currentStoryId, sceneId, sectionPersonBody(mode));
    if (board.reload) await board.reload({ silent: true });
  };

  // Suggested path: the next sequence to approve, then the next scene to draft.
  const { path: bookPath, status: bookPathStatus } = useBookPath(
    currentStoryId,
    (board.columns || []).length,
  );
  const pathTip = bookPathTip(bookPath, { stage: 'plot', storyId: currentStoryId });
  const onPathStep = async (tip) => {
    if (!tip?.sequence || !currentStoryId) return;
    try {
      const opened = await plotDossiersApi.openWorkshopBeat(currentStoryId, tip.sequence);
      if (opened?.session_id) navigate(`/chat/${opened.session_id}`);
    } catch {
      // the Plot Workshop button still opens it
    }
  };

  const journeyTip = journeyNextTip({
    stage: 'plottrack',
    storyId: currentStoryId,
    selectedColumn: selected?.column,
    selectedCell: selected?.cell,
    outlineColumns: board.columns,
    outlineCells: board.cells,
  });
  // A selected scene's panel has its own "Draft this scene" (with the POV
  // check), so the tip above shows only the book's next step. Run 43: two
  // "Draft this scene" controls for the same scene.
  const nextTip = selected?.column ? pathTip : (pathTip || journeyTip);

  const selectedColumn = selected?.column;
  const selectedSequence = selectedColumn
    ? columnIsSequence(selectedColumn, cellsForColumn(selectedColumn, board.cells))
    : false;
  const selectedTitle = selectedColumn
    ? columnDisplayName(selectedColumn, cellsForColumn(selectedColumn, board.cells))
    : '';
  const selectedDraftGate = selectedColumn
    ? columnNeedsDraftGate(selectedColumn, board.cells)
    : false;
  const selectedImport = importedSceneFor(gaps, selectedColumn?.scene_id);

  const pathFrameworkNames = Array.isArray(bookPath?.framework_sequences)
    ? bookPath.framework_sequences
    : [];
  const sequenceNameSource = frameworkNames.length ? frameworkNames : pathFrameworkNames;
  const sequenceNames = namedSequenceTitles(board.headers, sequenceNameSource);
  const hasOutline = (board.columns || []).length > 0;
  // A saved Framework is the outline even when no scene exists and the
  // sequence names are still loading or came back empty. The start screen
  // is only for a book with neither scenes nor a Framework.
  const frameworkSaved = frameworkHeld || frameworkRefIsSet(board.framework_ref);
  const showSequenceGrid = hasOutline || sequenceNames.length > 0 || frameworkSaved;
  // Grid first: the build and arrange tools open in the side panel. A scene's
  // inspector takes the panel while a cell is selected.
  // A framework can name sequences before any scene exists. Generate still
  // has to be visible then, or its result is painted into a hidden panel.
  const showingPlan = Boolean(planPart || planPinned);
  // A handoff has to open Build even when the grid is only Framework
  // sequence names, and an empty Generate result must stay visible, so a
  // chosen tool panel opens whether or not the grid has an outline yet.
  const toolOpen = showingPlan ? 'build' : ((hasOutline || toolPanel) ? toolPanel : null);
  const showPanel = !narrow && (Boolean(selected) || Boolean(toolOpen));

  const generatePanel = (
    <PlotTrackGenerate
      board={board}
      startGenerate={startGenerate}
      handoffId={handoffId}
      projectId={projectId}
      isSeries={isSeries}
      bookPath={bookPath}
      bookPathStatus={bookPathStatus}
      onNotesChanged={() => setOutlineNonce((nonce) => nonce + 1)}
      quietUntilScene={!hasOutline}
      planPart={planPart}
      onOutcome={rememberOutcome}
      onResult={keepGenerateOpen}
      onResumePending={() => {
        setPlanPart(null);
        setPlanPinned(false);
        setSelected(null);
        keepGenerateOpen();
      }}
      onPlanPartCancel={() => {
        setPlanPart(null);
        setPlanPinned(false);
        // A cost confirm used to close Build and hide every waiting draft.
        if (Object.keys(readPendingOffers(currentStoryId)).length > 0) {
          setToolPanel('build');
          setShowSuggest(true);
          return;
        }
        setToolPanel(null);
      }}
      onPlanPartStart={() => {
        setPlanPinned(true);
        setPlanPart(null);
        setToolPanel('build');
        setSelected(null);
      }}
    />
  );
  const buildTools = (
    <>
      {generatePanel}
      <PlotTrackImportMap
        storyId={currentStoryId}
        onChanged={() => board.reload?.()}
      />
    </>
  );
  const arrangeTools = (
    <>
      <PlotTrackTray
        tray={board.tray}
        sequences={sequences}
        onPlace={board.placeScene}
      />
      {board.addScene && (
        <PlotTrackAddScene
          columns={board.columns}
          cells={board.cells}
          sequences={sequences}
          defaultAfterSceneId={selected?.column?.scene_id}
          onAdd={board.addScene}
          onSuggestTitle={board.suggestSceneTitle}
        />
      )}
      {hasOutline && <PlotTrackAddRow rows={board.rows} onAdd={board.addRow} />}
      <PlotTrackChapterSeed
        columns={board.columns}
        suggestions={board.chapterSuggestions}
        onSuggest={board.suggestChapterBreaks}
        onAccept={board.acceptChapterSuggestion}
        onDismiss={board.dismissChapterSuggestion}
        onPlace={board.commitChapterBreak}
      />
      {showWhen && board.renameWhenLabel && (
        <PlotTrackWhenLabels
          labels={whenLabels}
          onRename={board.renameWhenLabel}
          onReorder={board.reorderWhenLabels}
          onDelete={board.deleteWhenLabel}
        />
      )}
    </>
  );
  // The Generate panel stays mounted while hidden: closing the panel mid-run
  // must not drop a paid result or a pending offer.
  const toolBody = (
    <>
      <div className="pt-side-panel__tool" hidden={toolOpen !== 'build' || Boolean(selected)}>
        {buildTools}
      </div>
      {toolOpen === 'arrange' && !selected && (
        <div className="pt-side-panel__tool">{arrangeTools}</div>
      )}
    </>
  );
  const toolTitle = toolOpen === 'arrange' ? PT_TOOL_ARRANGE : PT_TOOL_BUILD;
  const openTool = (name) => {
    setSelected(null);
    setToolPanel((current) => (current === name ? null : name));
  };

  const turnOnGapFill = async () => {
    if (!currentStoryId) return;
    setGapNote('');
    try {
      await plotDossiersApi.setGapFill(currentStoryId, true);
      setGapNote(OUTLINE_MORE_GAPS_ON);
    } catch {
      setGapNote(OUTLINE_MORE_GAPS_FAILED);
    }
  };

  const viewControls = (closeMore) => (
    <div className="pt-outline-view__menu">
      <div className="pt-outline-view">
        <label className="pt-outline-view__label" htmlFor="pt-outline-view">{OUTLINE_VIEW_AS}</label>
        <select
          id="pt-outline-view"
          className="pt-outline-view__select"
          value={outlineView}
          onChange={(event) => {
            chooseOutlineView(event.target.value);
            setViewMenuOpen(false);
            closeMore?.();
          }}
        >
          <option value="cards">{OUTLINE_VIEW_CARDS}</option>
          <option value="grid">{OUTLINE_VIEW_GRID}</option>
        </select>
      </div>
      {outlineView === 'grid' && (
        <div className="pt-outline-view">
          <label className="pt-outline-view__label" htmlFor="pt-outline-grid-size">{OUTLINE_GRID_SIZE}</label>
          <select
            id="pt-outline-grid-size"
            className="pt-outline-view__select"
            value={outlineGridSize}
            onChange={(event) => chooseOutlineGridSize(event.target.value)}
          >
            <option value="comfortable">{OUTLINE_GRID_SIZE_COMFORTABLE}</option>
            <option value="compact">{OUTLINE_GRID_SIZE_COMPACT}</option>
          </select>
        </div>
      )}
      {board.setSceneWhen && (
        <button
          type="button"
          className="pt-when__toggle"
          aria-pressed={showWhen}
          onClick={() => setWhenToggle(!showWhen)}
        >
          {showWhen ? PT_WHEN_HIDE : PT_WHEN_SHOW}
        </button>
      )}
      {/* A filter, not the picker. Runs 15 and 17: testers read a "POV character" list of
          only the people already on scenes as the place to choose Kane, and couldn't. */}
      <div className="pt-whose-head">
        <label htmlFor="pt-whose-head-filter">{PT_POV_FILTER_LABEL}</label>
        <select
          id="pt-whose-head-filter"
          value={whoseHead}
          onChange={(e) => setWhoseHead(e.target.value)}
          aria-describedby="pt-whose-head-filter-hint"
          title={PT_POV_FILTER_HINT}
        >
          <option value="">{PT_POV_FILTER_ALL}</option>
          {filterPeople.length > 0 && (
            <optgroup label={PT_POV_FILTER_GROUP}>
              {filterPeople.map((person) => (
                <option key={person.id} value={person.id}>{PT_POV_FILTER_PERSON(person.name)}</option>
              ))}
            </optgroup>
          )}
          {namedPeople.length > 0 && (
            <optgroup label={PT_PEOPLE_FILTER_GROUP}>
              {namedPeople.map((person) => (
                <option key={person.id} value={`${WITH_FILTER_PREFIX}${person.id}`}>
                  {PT_NAMING_FILTER_PERSON(person.name)}
                </option>
              ))}
            </optgroup>
          )}
          {whenLabels.length > 0 && (
            <optgroup label={PT_WHEN_FILTER_GROUP}>
              {whenLabels.map((label) => (
                <option key={label.id} value={`${WHEN_FILTER_PREFIX}${label.id}`}>{label.label}</option>
              ))}
            </optgroup>
          )}
        </select>
        <span id="pt-whose-head-filter-hint" className="pt-whose-head__hint">{PT_POV_FILTER_HINT}</span>
      </div>
      {board.setColumnWidth && (
        <div className="pt-width">
          <label className="pt-width__label" htmlFor="pt-width-select">{PT_WIDTH_LABEL}</label>
          <select
            id="pt-width-select"
            className="pt-width__select"
            value={board.columnWidth || 'standard'}
            onChange={(e) => board.setColumnWidth(e.target.value)}
          >
            {[['narrow', PT_WIDTH_NARROW], ['standard', PT_WIDTH_STANDARD], ['wide', PT_WIDTH_WIDE]].map(([value, text]) => (
              <option key={value} value={value}>{PT_WIDTH_OPTION(text)}</option>
            ))}
          </select>
          {(board.columns || []).some((column) => column.width_px) && (
            <button type="button" className="pt-width__btn pt-width__btn--quiet" onClick={board.resetColumnWidths}>
              {PT_WIDTH_RESET}
            </button>
          )}
        </div>
      )}
    </div>
  );

  const moreItems = [
    {
      id: 'view',
      label: OUTLINE_MORE_VIEW,
      keepOpen: true,
      onSelect: () => setViewMenuOpen((open) => !open),
      panel: viewMenuOpen ? viewControls : null,
    },
    {
      id: 'pov',
      label: OUTLINE_MORE_POV,
      onSelect: () => setShowPov(true),
    },
    {
      id: 'rules',
      label: OUTLINE_MORE_RULES,
      onSelect: () => navigate('/story-rules'),
    },
    ...(hasOutline ? [{
      id: 'arrange',
      label: PT_TOOL_ARRANGE,
      onSelect: () => openTool('arrange'),
    }] : []),
    {
      id: 'check',
      label: OUTLINE_MORE_CHECK,
      disabled: !hasOutline,
      onSelect: () => { if (hasOutline) openTool('build'); },
    },
    {
      id: 'gaps',
      label: OUTLINE_MORE_GAPS,
      disabled: !currentStoryId,
      onSelect: turnOnGapFill,
    },
    {
      id: 'deleted',
      label: RECENTLY_DELETED,
      onSelect: () => setShowDeleted(true),
    },
  ];

  const onFrameworkHung = (dto) => {
    if (dto) {
      frameworkHungStoryRef.current = currentStoryId;
      frameworkClearedStoryRef.current = null;
      frameworkNamesStoryRef.current = currentStoryId;
      const names = beatNames(dto);
      if (names.length) setFrameworkNames(names);
      setFrameworkHeld(true);
    } else {
      frameworkHungStoryRef.current = null;
      frameworkClearedStoryRef.current = currentStoryId;
      frameworkNamesStoryRef.current = currentStoryId;
      setFrameworkNames([]);
      setFrameworkHeld(false);
    }
    board.reload?.({ silent: true });
    setOutlineNonce((nonce) => nonce + 1);
    noteBookPathChanged();
  };
  const openBuild = () => {
    setSelected(null);
    setToolPanel('build');
    setShowSuggest(true);
  };

  // Hold the pill until this visit's path has landed. A null path used to
  // paint "Next: write scene 1" on the way back from Write.
  const nextWrite = bookPathStatus === 'ready' && !allScenesAccepted(bookPath)
    ? outlineNextWrite(bookPath, board.columns, {
      headers: board.headers,
      frameworkNames: sequenceNameSource,
    })
    : null;
  const generateInView = showSequenceGrid
    ? Boolean(toolOpen === 'build' && !selected && (narrow || showPanel))
    : Boolean(showSuggest);
  // #2321: once a Framework is on the book, the sequence count stays visible.
  // It used to ride the empty-book tip, so a book that already had scenes lost it.
  const frameworkApplied = frameworkSaved || Boolean(bookPath?.has_framework);
  const sequenceCountLine = frameworkApplied ? bookPathProgress(bookPath) : '';
  const noticeTip = sequenceCountLine && nextTip?.text
    ? {
      ...nextTip,
      text: nextTip.text.startsWith(sequenceCountLine)
        ? nextTip.text.slice(sequenceCountLine.length).trim()
        : nextTip.text,
    }
    : nextTip;
  const noticeKey = pickOutlineNotice({
    outcome: Boolean(generateOutcome?.text) && !generateInView,
    upload: Boolean(presentGaps(gaps, 'plot', { povSlot: povNeedsSettle(bookPov) })),
    continuity: spanFlags.length > 0,
    when: timelineOffers(board.columns).length > 0 && Boolean(board.applyWhenTimelines),
    gap: Boolean(gapNote),
    journey: !hasOutline && Boolean(nextTip?.text || nextTip?.action),
  });

  return (
    <div className="pt-layout pt-layout--grid-first">
      <header className="pt-layout__header">
        <div className="pt-layout__heading">
          <div className="pt-layout__identity">
            <h1 className="pt-layout__title">{OUTLINE_ROOM_TITLE}</h1>
            {showSequenceGrid && (
              <BookStructureDoor
                fitTitle
                storyId={currentStoryId}
                frameworkKnown
                initialFrameworkRef={board.framework_ref || null}
                onHung={onFrameworkHung}
              />
            )}
            <BookToolsGroup />
          </div>
          <OutlineMoreMenu items={moreItems} />
          {hasOutline && nextWrite && (
            <button
              type="button"
              className="pt-next-write"
              onClick={() => navigate(draftThisScenePath(
                currentStoryId,
                nextWrite.column
                  ? columnHandoff(nextWrite.column)
                  : { sceneId: nextWrite.sceneId, title: nextWrite.title },
              ))}
            >
              {nextWrite.label}
            </button>
          )}
        </div>
        {showPov && (
          <div className="pt-layout__pov" id="pt-book-pov">
            <NarrativePovStrip
              storyId={currentStoryId}
              people={povPeople}
              onChange={setBookPov}
              onSaved={() => setOutlineNonce((nonce) => nonce + 1)}
            />
          </div>
        )}
      </header>

      <div className="pt-layout__notices">
        {sequenceCountLine && (
          <p className="pt-layout__how pt-layout__count" role="status">{sequenceCountLine}</p>
        )}
        {noticeKey === 'upload' && (
          <UploadSettle
            gaps={gaps}
            room="plot"
            povSlot={povNeedsSettle(bookPov)}
            onFocusScene={onFocusScene}
            onOpenNotes={onOpenNotes}
          />
        )}
        {noticeKey === 'continuity' && (
          <div className="pt-layout__continuity" role="status">
            <p>{SPAN_CONTINUITY_LEDE}</p>
            {continuityLines.length > 0 && (
              <ul>
                {continuityLines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        {noticeKey === 'when' && (
          <TimelineOffer
            offers={timelineOffers(board.columns)}
            onUse={board.applyWhenTimelines}
            onDone={() => setWhenToggle(true)}
          />
        )}
        {noticeKey === 'gap' && <p className="pt-layout__how" role="status">{gapNote}</p>}
        {noticeKey === 'journey' && (
          <div className="pt-layout__next">
            <JourneyNextTip tip={noticeTip} onAction={onPathStep} />
          </div>
        )}
        {noticeKey === 'outcome' && generateOutcome && (
          <p
            className={
              generateOutcome.kind === 'error'
                ? 'pt-generate__error'
                : (generateOutcome.kind === 'done' ? 'pt-generate__done' : 'pt-generate__empty')
            }
            role={generateOutcome.kind === 'done' ? 'status' : 'alert'}
            data-testid="pt-generate-outcome"
          >
            {generateOutcome.text}
          </p>
        )}
      </div>

      {!showSequenceGrid ? (
        <div className="pt-layout__empty">
          <p className="pt-layout__how">{EMPTY_GRID_LEAD}</p>
          <div className="pt-empty-choices">
            <section className="pt-empty-card">
              <h2>{OUTLINE_EMPTY_WRITE_TITLE}</h2>
              <p>{OUTLINE_EMPTY_WRITE_BODY}</p>
              {board.addScene && (
                <PlotTrackAddScene
                  columns={board.columns}
                  cells={board.cells}
                  sequences={sequences}
                  draftKey={currentStoryId ? `add:${currentStoryId}:empty` : ''}
                  onAdd={board.addScene}
                  onSuggestTitle={board.suggestSceneTitle}
                />
              )}
            </section>
            <section className="pt-empty-card">
              <h2>{OUTLINE_EMPTY_FRAMEWORK_TITLE}</h2>
              <p>{OUTLINE_EMPTY_FRAMEWORK_BODY}</p>
              <BookStructureDoor
                storyId={currentStoryId}
                frameworkKnown
                initialFrameworkRef={board.framework_ref || null}
                onHung={onFrameworkHung}
              />
            </section>
          </div>
          {showSuggest && buildTools}
        </div>
      ) : (
        <div className="pt-layout__body">
          <div className={`pt-layout__main${outlineView === 'grid' ? ' pt-layout__main--grid' : ''}`}>
            {narrow && <div className="pt-layout__tools-inline">{toolBody}</div>}
            {chapterError && (
              <p className="pt-card-rows__error" role="alert">{chapterError}</p>
            )}
            {outlineView === 'grid' ? (
              <PlotTrackGrid
                rows={board.rows}
                columns={visibleColumns}
                size={outlineGridSize}
                lineColumns={board.columns}
                cells={board.cells}
                headers={board.headers}
                unplannedSequences={hasOutline ? [] : sequenceNames}
                frameworkNames={sequenceNameSource}
                frameworkApplied={frameworkApplied}
                onAddScene={board.addScene}
                onSuggestTitle={board.suggestSceneTitle}
                storyId={currentStoryId}
                onOpenBuild={openBuild}
                showWhen={showWhen}
                whenLabels={whenLabels}
                onSetWhen={board.setSceneWhen}
                onCreateWhen={board.createWhenLabel}
                columnWidth={board.columnWidth}
                onResizeColumn={board.setOneColumnWidth}
                onSelectCell={(row, column, cell) => { onSelectCell(row, column, cell); }}
                onOpenScene={openSceneColumn}
                selected={selected}
                onRenameRow={board.saveRow
                  ? (rowId, label) => board.saveRow(rowId, { label })
                  : undefined}
                onChangeVisibility={board.saveRow
                  ? (rowId, visibility) => board.saveRow(rowId, { visibility })
                  : undefined}
                onChangeColor={board.saveRow
                  ? (rowId, color) => board.saveRow(rowId, { color })
                  : undefined}
                onRenameScene={board.editScene
                  ? (sceneId, title) => board.editScene({ sceneId, title })
                  : undefined}
                onMoveScene={board.editScene
                  ? (sceneId, place) => placeScene(
                    sceneId,
                    typeof place === 'string' ? { sequenceId: place, atStart: true } : place,
                  )
                  : undefined}
                onStartChapter={board.commitChapterBreak}
                onChatScene={(sceneName, sceneId) => publishChatFocus(sceneName, sceneId)}
                onDeleteScene={board.deleteScene
                  ? async (sceneId) => {
                    const openSceneId = selected?.column?.scene_id;
                    await board.deleteScene(sceneId);
                    publishChatSceneRemoved(sceneId);
                    if (openSceneId && String(openSceneId) === String(sceneId)) {
                      setSelected(null);
                    }
                    setUndo({
                      message: SCENE_DELETED,
                      run: async () => {
                        await restoreRecentlyDeleted(currentStoryId, 'scene', sceneId);
                        if (board.reload) await board.reload({ silent: true });
                      },
                    });
                  }
                  : undefined}
                onEditHeader={board.setHeaderSpan ? onSaveSpan : undefined}
              />
            ) : (
            <OutlineCardRows
              rows={board.rows}
              columns={visibleColumns}
              lineColumns={board.columns}
              cells={board.cells}
              headers={board.headers}
              frameworkNames={sequenceNameSource}
              frameworkApplied={frameworkApplied}
              showWhen={showWhen}
              whenLabels={whenLabels}
              onSetWhen={board.setSceneWhen}
              onCreateWhen={board.createWhenLabel}
              columnWidth={board.columnWidth}
              onResizeColumn={board.setOneColumnWidth}
              onSelectCell={(row, column, cell) => { onSelectCell(row, column, cell); }}
              onOpenScene={openSceneColumn}
              selected={selected}
              onChangeRowColor={board.saveRow
                ? (rowId, color) => board.saveRow(rowId, { color })
                : undefined}
              onRenameScene={board.editScene
                ? (sceneId, title) => board.editScene({ sceneId, title })
                : undefined}
              onMoveScene={board.editScene
                ? (sceneId, place) => placeScene(
                  sceneId,
                  typeof place === 'string' ? { sequenceId: place, atStart: true } : place,
                )
                : undefined}
              onReorderScene={board.editScene
                ? (sceneId, place, origin) => placeScene(sceneId, place, origin)
                : undefined}
              storyId={currentStoryId}
              onStartChapter={board.commitChapterBreak}
              onRemoveChapter={board.clearChapterBreak
                ? async (chapter) => {
                  const afterSceneId = chapter?.closed_at_scene_id
                    || (chapter?.scene_ids || [])[(chapter?.scene_ids || []).length - 1];
                  const name = String(chapter?.display_name || '').trim();
                  try {
                    await board.clearChapterBreak(chapter.id);
                    setChapterError('');
                    if (!afterSceneId) return;
                    setUndo({
                      message: CHAPTER_BREAK_REMOVED,
                      run: async () => {
                        try {
                          if (board.commitChapterBreak) {
                            await board.commitChapterBreak(afterSceneId, name);
                          }
                          setChapterError('');
                        } catch (err) {
                          setChapterError(err?.message || 'Request failed');
                        }
                      },
                    });
                  } catch (err) {
                    setChapterError(err?.message || 'Request failed');
                  }
                }
                : undefined}
              onChatScene={(sceneName, sceneId) => publishChatFocus(sceneName, sceneId)}
              onPlanPart={(partName) => {
                publishChatFocus(partName, '');
                setPlanPart({ name: partName });
                setPlanPinned(true);
                setToolPanel('build');
                setSelected(null);
              }}
              onDeleteScene={board.deleteScene
                ? async (sceneId) => {
                  const openSceneId = selected?.column?.scene_id;
                  await board.deleteScene(sceneId);
                  publishChatSceneRemoved(sceneId);
                  if (openSceneId && String(openSceneId) === String(sceneId)) {
                    setSelected(null);
                  }
                  setUndo({
                    message: SCENE_DELETED,
                    run: async () => {
                      await restoreRecentlyDeleted(currentStoryId, 'scene', sceneId);
                      if (board.reload) await board.reload({ silent: true });
                    },
                  });
                }
                : undefined}
              onAddScene={board.addScene}
              onSuggestTitle={board.suggestSceneTitle}
              onEditHeader={board.setHeaderSpan ? onSaveSpan : undefined}
            />
            )}
          </div>
          {!narrow && (
            <aside
              id="pt-side-panel"
              className="pt-side-panel"
              hidden={!showPanel}
              aria-label={selected ? (selectedTitle || WHOSE_HEAD) : toolTitle}
            >
              {selected && (
                <PlotTrackInspector
                  selection={selected}
                  onClose={() => setSelected(null)}
                  onSave={onSave}
                  onDraftScene={onDraftScene}
                  onSetWhoseHead={onSetWhoseHead}
                  onSetPerson={onSetPerson}
                  people={people}
                  castIds={castIdsFromBookPov(bookPov)}
                  sequenceSized={selectedSequence}
                  columnTitle={selectedTitle}
                  draftGate={selectedDraftGate}
                  importedScene={selectedImport}
                  onSavePurpose={onSavePurpose}
                  onRenameScene={onRenameScene}
                  onEditScene={board.editScene ? onEditScene : undefined}
                  sequences={sequences}
                  sceneTitles={sceneTitles}
                  scenePeople={selected?.column ? (resolvedColumn()?.people || []) : null}
                  peoplePool={board.peoplePool}
                  onSetScenePerson={board.setScenePerson && selected?.column?.scene_id
                    ? (entryId, inScene) => board.setScenePerson(selected.column.scene_id, entryId, inScene)
                    : undefined}
                />
              )}
              {!selected && toolOpen && (
                <div className="pt-side-panel__header">
                    <h2 className="pt-side-panel__title">{toolTitle}</h2>
                    <button
                      type="button"
                      className="pt-side-panel__close"
                      onClick={() => {
                        setToolPanel(null);
                        setPlanPinned(false);
                      }}
                    >
                      {PT_PANEL_CLOSE}
                    </button>
                </div>
              )}
              {toolBody}
            </aside>
          )}
        </div>
      )}


      {narrow && (
        <BottomSheet
          isOpen={Boolean(selected)}
          onClose={() => setSelected(null)}
          title={selectedTitle || WHOSE_HEAD}
        >
          <div className="pt-scene-sheet" data-testid="pt-scene-sheet">
            {selected && (
              <div className="pt-scene-sheet__heads">
                <WhoseHeadControl
                  id="pt-whose-head-sheet"
                  value={whoseHeadValue(selected.column)}
                  people={people}
                  castIds={castIdsFromBookPov(bookPov)}
                  onChange={(next) => selected.column?.scene_id && onSetWhoseHead(selected.column.scene_id, next)}
                />
                <PersonControl
                  id="pt-person-sheet"
                  person={selected.column?.person}
                  onChange={(mode) => selected.column?.scene_id && onSetPerson(selected.column.scene_id, mode)}
                />
              </div>
            )}
            <PlotTrackInspector
              selection={selected}
              onClose={() => setSelected(null)}
              onSave={onSave}
              onDraftScene={onDraftScene}
              onSetWhoseHead={onSetWhoseHead}
              onSetPerson={onSetPerson}
              people={people}
              castIds={castIdsFromBookPov(bookPov)}
              showWhoseHead={false}
              showPerson={false}
              sequenceSized={selectedSequence}
              columnTitle={selectedTitle}
              draftGate={selectedDraftGate}
              importedScene={selectedImport}
              onSavePurpose={onSavePurpose}
              onRenameScene={onRenameScene}
              onEditScene={board.editScene ? onEditScene : undefined}
              sequences={sequences}
              sceneTitles={sceneTitles}
            />
          </div>
        </BottomSheet>
      )}

      <DraftPackWarn
        open={packWarn}
        onCancel={() => setPackWarn(false)}
        onContinue={continueAfterPackWarn}
      />
      <UndoMessage
        message={undo?.message}
        onUndo={undo?.run
          ? () => {
            const run = undo.run;
            setUndo(null);
            void run();
          }
          : null}
        onDismiss={() => setUndo(null)}
        onRecentlyDeleted={() => setShowDeleted(true)}
      />
      {showDeleted && (
        <RecentlyDeleted
          storyId={currentStoryId}
          onRestored={async () => {
            if (board.reload) await board.reload({ silent: true });
          }}
        />
      )}
    </div>
  );
}

export function OutlineMoreMenu({ items, extra = null }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  const place = useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const b = btn.getBoundingClientRect();
    const top = b.bottom + 4;
    const width = menuRef.current?.offsetWidth || 0;
    const layout = btn.closest('.pt-layout');
    const limit = layout
      ? Math.min(window.innerWidth - 8, layout.getBoundingClientRect().right - 8)
      : window.innerWidth - 8;
    let left = b.left;
    if (left + width > limit) left = b.right - width;
    if (left + width > limit) left = limit - width;
    if (left < 8) left = 8;
    const maxWidth = Math.max(240, limit - 8);
    setPos((prev) => (
      prev && prev.top === top && prev.left === left && prev.maxWidth === maxWidth
        ? prev
        : { top, left, maxWidth }
    ));
  }, []);
  useEffect(() => {
    if (!open) {
      setPos(null);
      return undefined;
    }
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);
  // Re-measure after each render while open: the View panel changes the width.
  useEffect(() => {
    if (open) place();
  });
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (btnRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        btnRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const menu = open && (
    <div
      ref={menuRef}
      className="pt-outline-more__menu"
      role="menu"
      aria-label={OUTLINE_MORE}
      style={{
        top: pos ? pos.top : 0,
        left: pos ? pos.left : 0,
        maxWidth: pos ? pos.maxWidth : undefined,
        maxHeight: pos ? `calc(100vh - ${pos.top}px - 8px)` : undefined,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      {items.map((item) => (
        <React.Fragment key={item.id}>
          <button
            type="button"
            role="menuitem"
            className="pt-outline-more__item"
            disabled={item.disabled}
            onClick={() => {
              if (item.disabled) return;
              if (!item.keepOpen) setOpen(false);
              item.onSelect?.();
            }}
          >
            {item.label}
          </button>
          {typeof item.panel === 'function' ? item.panel(close) : item.panel}
        </React.Fragment>
      ))}
      {extra}
    </div>
  );
  return (
    <div className="pt-outline-more">
      <button
        ref={btnRef}
        type="button"
        className="pt-toolbar__btn"
        aria-label={OUTLINE_MORE}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {OUTLINE_MORE_VISIBLE}
      </button>
      {menu ? ReactDOM.createPortal(menu, document.body) : null}
    </div>
  );
}

function DraftPackWarn({ open, onCancel, onContinue }) {
  if (!open) return null;
  return (
    <BottomSheet isOpen={open} onClose={onCancel} title={DRAFT_PACK_TITLE}>
      <div className="whose-head-warn" data-testid="draft-pack-warn">
        <p>{DRAFT_PACK_WARN}</p>
        <div className="whose-head-warn__actions">
          <button type="button" onClick={onCancel}>{WHOSE_HEAD_GO_BACK}</button>
          <button type="button" className="whose-head-warn__continue" onClick={onContinue}>
            {DRAFT_PACK_ANYWAY}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}
